import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { AddressModel } from '@models/address';
import { ModelReponseWithVariantsModelFromApi } from '@models/api-models/ModelReponseWithVariantsModelFromApi';
import { CaseAlternativeGroupLineModel } from '@models/api-models/case-alternative-groupline';
import { ModelModel } from '@models/api-models/model';
import { OrderCreateModel } from '@models/api-models/order-create-model';
import { CustomerModel } from '@models/customer';
import { CCDiscountModel, DiscountModel, RCDiscountModel, SFDiscountModel } from '@models/discounts/discount-model';
import { FairModel } from '@models/fair-model';
import { MiscItemCreateModel } from '@models/misc-item-create';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { Order } from '@models/order';
import { OrderCompleteModel } from '@models/order-complete-model';
import { OrderInfoModel } from '@models/order-info.model';
import { OrderItemMiscModel, OrderItemMiscState } from '@models/order-item-misc-model';
import { OrderItemType } from '@models/order-item-type';
import { OrderState } from '@models/order-state';
import { OrderUpdateModel } from '@models/order-update-model';
import { OrderIdsModel } from '@models/orderIds-model';
import { OrdersModel } from '@models/orders';
import { PriceModel, PriceModelStatus, PriceResponse } from '@models/price-model';
import { StartKitModel } from '@models/start-kit-model';
import { TagModel } from '@models/tag';
import { VariantColorModel } from '@models/variant-color';
import { VersionModel } from '@models/version-model';
import { AppState } from '@shared/app-state';
import { UserTypeConstant } from '@shared/user-type-constants';
import { plainToClass } from "class-transformer";
import { Observable } from 'rxjs';
import { GuidService } from './guid.service';
import { HttpService } from './http.service';
import { MenuService } from './menu.service';
import { ServerInfoService } from './server-info.service';
import { StoreService } from './store.service';
import { UserService } from './user.service';
import { ModelsService } from './v2/models.service';
import { OrderServiceV2 } from './v2/order.service';

@Injectable({
	providedIn: 'root'
})
export class DataApiService {

	constructor(
		private userService: UserService,
		private appState: AppState,
		private storeService: StoreService,
		private httpService: HttpService,
		private modelsService: ModelsService,
		private menuService: MenuService,
		private serverInfoService: ServerInfoService,
		private guidService: GuidService,
		private orderServiceV2: OrderServiceV2
	) {
	}

	public async loadDataRequests(modelgroup: ModelGroupModel): Promise<Observable<HttpEvent<ModelReponseWithVariantsModelFromApi>>> {
		try {
			if (modelgroup.ParentModelGroupUuid) {
				await this.modelsService.getModelGroup(modelgroup.ParentModelGroupUuid);
			}

			await this.menuService.getMenu(modelgroup);
			await this.getAllSizes(modelgroup.ProductLineUuid, modelgroup.Uuid);
			await this.modelsService.getVariantsOnModelgroup(modelgroup);
			return this.modelsService.getModelsWithAllDataRequest(modelgroup);
		}
		catch (error) {
			throw error;
		}
	}

	public async getStartKits(): Promise<Array<StartKitModel> | undefined> {
		try {
			const url = `${environment.dataUrl}/StartKits`;
			const result = await this.httpService.getDataWithOfflineCheck<StartKitModel>(url);
			return result?.map((x: StartKitModel) => plainToClass(StartKitModel, x));
		}
		catch (error) {
			throw (error);
		}
	}

	public async getOwnAddresses(): Promise<Array<AddressModel> | undefined> {
		const result = await this.storeService.getOrSetFromStore<Array<AddressModel>>(`${environment.dataUrl}/Account/Addresses/`);
		return result;
	}

	public async getDiscounts(customer: CustomerModel | null, isEnduser: boolean): Promise<Array<DiscountModel>> {
		return new Promise((resolve, reject) => {
			if (!customer || !customer.isConsultantOrderOnBehalf) {
				resolve([]);
				return;
			}

			const discounts = [new CCDiscountModel(), new RCDiscountModel()];

			if (isEnduser) {
				discounts.push(new SFDiscountModel());
			}

			resolve(discounts);
		});
	}

	public async getFairs(): Promise<Array<FairModel>> {
		const fairsData = await this.httpService.getDataWithOfflineCheck<Array<FairModel>>(`${environment.dataUrl}/Fairs/`);
		const fairs = fairsData.map((x: FairModel) => plainToClass(FairModel, x));
		return fairs;
	}

	public async SearchColors(search: string): Promise<Array<VariantColorModel> | undefined> {
		const allColorsResult = await this.storeService.getOrSetFromStore<Array<VariantColorModel>>(`${environment.dataUrl}/colorsstartswithcode?code=${search?.toLowerCase()}`);

		const filteredColorsResult = allColorsResult?.map(x => plainToClass(VariantColorModel, x));

		return filteredColorsResult;
	}

	public async searchModels(search?: string, tags?: Array<TagModel>, hboxFrom?: number, hboxTo?: number): Promise<Array<ModelModel | null> | undefined> {
		let tagUrl = `&requestModel.hboxFrom=${hboxFrom || ""}&requestModel.hboxTo=${hboxTo || ""}`;

		tags = tags || [];
		tags.forEach(tag => {
			tagUrl += "&requestModel.tags=" + tag.Id;
		});

		const result = await this.storeService.getOrSetFromStore<Array<ModelModel>>(`${environment.dataUrl}/models/search?requestModel.searchCriteria=${search || ""}${tagUrl}`);
		return result?.map((x) => ModelModel.createCopy(x));
	}

	public async getSizes(productLineUuid?: string, modelGroupUuid?: string, modelUuid?: string): Promise<Array<ModelSizeModel> | undefined> {
		const result = await this.getAllSizes(productLineUuid, modelGroupUuid);
		return result?.filter(x => x.ModelUuid == modelUuid);
	}

	private async getAllSizes(productLineUuid?: string, modelGroupUuid?: string): Promise<Array<ModelSizeModel> | undefined> {
		// productLineUuid and modelGroupUuid are actually required in the backend call, and if they are not defined
		// the backend will throw exceptions. But to avoid too many changes in the calling methods, we just ignore it for now
		// and return an empty array.
		if (!productLineUuid || !modelGroupUuid) {
			return [];
		}

		const result = await this.storeService.getOrSetFromStore<Array<ModelSizeModel>>(`${environment.dataUrl}/modelsize?requestModel.productLineUuid=${productLineUuid}&requestModel.modelGroupUuid=${modelGroupUuid}`);
		return result?.map((x) => plainToClass(ModelSizeModel, x));
	}

	public async getAllCases() {
		let allCases = await this.storeService.getOrSetFromStore<Array<CaseAlternativeGroupLineModel>>(`${environment.dataUrl}/cases`);

		return allCases;
	}

	public async completeOrder(orders: Array<OrderCompleteModel>): Promise<void> {
		await this.httpService.postWithOfflineHandling<void>(`${environment.dataUrl}/orders/complete`, orders);
	}

	public async requestOrder(orders: Array<OrderCompleteModel>, isNewPartner = false): Promise<void> {
		await this.httpService.postWithOfflineHandling<void>(`${environment.dataUrl}/orders/request?isNewPartner=${isNewPartner}`, orders);
	}

	public async createNewOrder(order: OrderCreateModel, isNewPartner: boolean): Promise<number> {
		const result = await this.httpService.postWithOfflineHandling<number>(`${environment.dataUrl}/orders/createNew?isNewPartner=${isNewPartner}`, order);
		return result;
	}

	public async setOrderData(orderId: number, model: OrderUpdateModel): Promise<void> {
		await this.httpService.postWithOfflineHandling<number>(`${environment.dataUrl}/orders/${orderId}`, model);
	}

	public async getOrderIds(customerNo: string | undefined | null, crmTicketId: string | undefined): Promise<Array<OrderIdsModel> | null> {
		const orders = await this.httpService.getDataWithOfflineCheck<Array<OrderIdsModel>>(`${environment.dataUrl}/orders/ActiveOrderIds/${customerNo || ""}/${crmTicketId || ""}`);

		if (orders) {
			return orders.map((x: OrderIdsModel) => plainToClass(OrderIdsModel, x));
		}

		return null;
	}

	/**
	 * Gets active orders using the V2 API for improved performance
	 * @param customerNo Optional customer number
	 * @returns A list of orders in the original model format
	 */
	public async getOrders(customerNo?: string | null): Promise<Array<OrderState>> {
		return await this.orderServiceV2.getOrders(customerNo);
	}

	public async postMiscItemFromState(orderItemMiscModelState: OrderItemMiscState): Promise<OrderItemMiscState> {
		const orderItemMiscModel = new OrderItemMiscModel(orderItemMiscModelState);
		return await this.postMiscItem(orderItemMiscModel);
	}

	public async postMiscItem(orderItemMiscModel: OrderItemMiscModel): Promise<OrderItemMiscState> {
		let miscItem: MiscItemCreateModel = MiscItemCreateModel.Create(orderItemMiscModel);

		const result = await this.httpService.postWithOfflineHandling<OrderItemMiscState>(`${environment.dataUrl}/orders/OrderItemMisc/Create`, miscItem);

		return result;
	}

	public async deleteOrderMiscItem(uuid: string): Promise<void> {
		return await this.httpService.deleteWithOfflineHandling<void>(`${environment.dataUrl}/orders/OrderItemMisc/${uuid}`);
	}

	public async getTags(): Promise<Array<TagModel> | undefined> {
		const result = await this.storeService.getOrSetFromStore<Array<TagModel>>(`${environment.dataUrl}/tags`);
		return result?.map((x) => plainToClass(TagModel, x));
	}

	public async getPrice(orderItemType: OrderItemType, orderItemUuid: string): Promise<PriceResponse> {
		let priceModel = new PriceResponse();

		try {
			if (this.appState.priceModuleEnabled.getValue()) {
				priceModel = await this.httpService.postWithOfflineHandling<PriceResponse>(`${environment.dataUrl}/price/${orderItemType}/${orderItemUuid}`, {})

				if (!priceModel.data) {
					priceModel.status = new PriceModelStatus(priceModel.status?.statusCode, priceModel.status?.message);
				}
			}
			else {
				priceModel.data = new PriceModel()
				priceModel.data.responseItems = []
			}

			return priceModel;
		}
		catch (err: any) {
			console.error('getPrice error', err);

			priceModel.status = new PriceModelStatus(err.status, err.error?.substring(0, err.error.indexOf('\r\n')));

			return priceModel;
		}
	}

	public async updatePricesOnOrder(orderUuid: string): Promise<PriceResponse> {
		return await this.httpService.postWithOfflineHandling<PriceResponse>(`${environment.dataUrl}/updateAndReturnPrices/${orderUuid}`, {})
	}

	public async getVersions(): Promise<Array<VersionModel>> {
		const endpoint = `${environment.dataUrl}/versions`;

		try {
			const versions = await this.httpService.getDataWithOfflineCheck<Array<VersionModel>>(endpoint);
			return versions;
		}
		catch (error) {
			throw error;
		}
	}

	/**
	 * Gets a specific order by calling the original API
	 * @param userUuid User UUID
	 * @param orderUuid Order UUID
	 * @param customerNo Customer number
	 * @returns The order wrapped in an OrdersModel, or null if not found
	 */
	public async getOrder(userUuid: string, orderUuid: string, customerNo: string): Promise<OrdersModel | null> {
		// Use the original endpoint instead of V2 service
		const orderData = await this.httpService.getDataWithOfflineCheck<OrderState>(
			`${environment.dataUrl}/orderconfirmation/${customerNo}/${orderUuid}`
		);

		// Transform the raw JSON data to properly typed OrderState with Date objects
		const order = orderData ? plainToClass(OrderState, orderData) : null;

		const user = await this.userService.GetUser();

		if (this.guidService.GUIDisEmpty(userUuid) && order?.ConsultantUuid && (order.ConsultantUuid.toLocaleLowerCase() !== userUuid.toLocaleLowerCase()) &&
			user?.Type !== UserTypeConstant.Employee) {
			return null;
		}

		const orders: Array<Order> = [];
		if (order) {
			order.ShowOrderConfirmation = true;
			orders.push(new Order(order));
		}

		return new OrdersModel(this.appState, orders);
	}

	/**
	 * Gets unfinished orders from the original API
	 * @param fromNetwork Whether to force fetching from the network
	 * @returns A list of unfinished orders or null if not found
	 */
	public async getUnfinishedOrders(fromNetwork: boolean = false): Promise<Array<OrderInfoModel> | null> {
		const url = `${environment.dataUrl}/orders/unfinished`;
		
		// Call the original endpoint instead of V2 service
		if (fromNetwork) {
			return await this.serverInfoService.getNetworkFirst<Array<OrderInfoModel>>(url);
		} else {
			const result = await this.httpService.getDataWithOfflineCheck<Array<OrderInfoModel>>(url);
			return result || null;
		}
	}

	/**
	 * Gets a chunk of orders from the original API
	 * @param skip Number of orders to skip
	 * @param take Number of orders to take
	 * @param searchString Optional search string
	 * @returns A list of orders or null if not found
	 */
	public async getOrderChunkAsync(skip: number, take: number, searchString: string | null = null): Promise<Array<OrderInfoModel> | null> {
		let url = `${environment.dataUrl}/orders/chunk/?skip=${skip}&take=${take}`;

		if (searchString) {
			url += `&searchString=${searchString}`;
		}

		// Call the original endpoint instead of V2 service
		return await this.serverInfoService.getNetworkFirst<Array<OrderInfoModel>>(url);
	}

	/**
	 * Gets an order by its AX ID from the original API
	 * @param axId AX ID
	 * @returns The order or null if not found
	 */
	public async getOrderByAxId(axId: string): Promise<Array<OrderInfoModel> | null> {
		// Call the original endpoint instead of V2 service
		try {
			return await this.serverInfoService.getNetworkFirst<Array<OrderInfoModel>>(
				`${environment.dataUrl}/order/${axId}`
			);
		} catch (error) {
			console.error('Error getting order by AX ID:', error);
			return null;
		}
	}

	public async deleteOrder(id?: number): Promise<void> {
		const result = await this.httpService.deleteWithOfflineHandling<void>(`${environment.dataUrl}/orders/${id}`);
		return result;
	}

}
